/**
 * Shared column definitions for the proforma-invoice "selected screens" table.
 * Used by MediaProformaInvoiceForBuyer and MediaProformaInvoiceForSeller so the
 * hoarding / DOOH / multiplex excel layouts stay in sync across both views.
 */

/** First non-empty value among the given keys on a media line-item row. */
export const pickMediaCell = (row, ...keys) => {
  for (const k of keys) {
    const v = row?.[k];
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
};

export const MULTIPLEX_PI_COLS = [
  { label: "State", pick: (r) => pickMediaCell(r, "state", "State") },
  {
    label: "Cinema category",
    pick: (r) => pickMediaCell(r, "cinemaCategory", "CinemaCategory"),
  },
  { label: "Cinema", pick: (r) => pickMediaCell(r, "cinema", "Cinema") },
  { label: "Audi #", pick: (r) => pickMediaCell(r, "audiNum", "AudiNum", "audi") },
  {
    label: "Seating capacity",
    pick: (r) => pickMediaCell(r, "seatingCapacity", "SeatingCapacity"),
  },
  {
    label: "Screen code",
    pick: (r) => pickMediaCell(r, "screenCode", "ScreenCode"),
  },
  { label: "CAS codes", pick: (r) => pickMediaCell(r, "casCodes", "CASCodes") },
  {
    label: "Upload codes",
    pick: (r) => pickMediaCell(r, "uploadCodes", "UploadCodes"),
  },
  { label: "Audi form", pick: (r) => pickMediaCell(r, "audiForm", "AudiForm") },
  { label: "MRP", pick: (r) => pickMediaCell(r, "PricePerUnit", "mrp", "MRP") },
  {
    label: "Discounted MRP",
    pick: (r) => pickMediaCell(r, "DiscountedPrice", "discountedPrice"),
  },
];

export const HOARDING_PI_COLS = [
  { label: "Name", pick: (r) => pickMediaCell(r, "name", "Name") },
  { label: "Area", pick: (r) => pickMediaCell(r, "area", "Area") },
  { label: "State", pick: (r) => pickMediaCell(r, "state", "State") },
  { label: "Landmark", pick: (r) => pickMediaCell(r, "landmark", "Landmark") },
  {
    label: "Days",
    pick: (r) =>
      pickMediaCell(r, "selectedDuration", "duration", "TimeLineToBought"),
  },
  { label: "Width", pick: (r) => pickMediaCell(r, "width", "Width") },
  { label: "Height", pick: (r) => pickMediaCell(r, "height", "Height") },
  {
    label: "Total Sq.ft",
    pick: (r) => pickMediaCell(r, "size", "Size", "totalSqft", "TotalSqft"),
  },
  { label: "MRP", pick: (r) => pickMediaCell(r, "mrp", "MRP", "PricePerUnit") },
  {
    label: "Discounted MRP",
    pick: (r) => pickMediaCell(r, "DiscountedPrice", "discountedPrice"),
  },
];

export const DIGITAL_ADS_PI_COLS = [
  {
    label: "Property",
    pick: (r) => pickMediaCell(r, "propertyName", "PropertyName"),
  },
  {
    label: "Site code",
    pick: (r) => pickMediaCell(r, "mediaSiteCode", "MediaSiteCode"),
  },
  { label: "State", pick: (r) => pickMediaCell(r, "state", "State") },
  { label: "Location", pick: (r) => pickMediaCell(r, "location", "Location") },
  {
    label: "Screens",
    pick: (r) => pickMediaCell(r, "numberOfScreens", "NumberOfScreens"),
  },
  {
    label: "Days",
    pick: (r) =>
      pickMediaCell(r, "selectedDuration", "duration", "TimeLineToBought"),
  },
  { label: "MRP", pick: (r) => pickMediaCell(r, "mrp", "MRP") },
  {
    label: "Discounted MRP",
    pick: (r) => pickMediaCell(r, "DiscountedPrice", "discountedPrice"),
  },
  { label: "Remarks", pick: (r) => pickMediaCell(r, "remarks", "Remarks") },
];

/**
 * Pick the right excel column set for the selected-screens table. Prefer the
 * backend media-type flags; fall back to the line-item row shape so multiplex
 * and DOOH render correctly even when the flags are missing (legacy orders).
 */
export function pickMediaPiCols(productLine, rows) {
  const sample = (rows || []).find(Boolean) || {};
  const hasField = (...keys) =>
    keys.some((k) => sample?.[k] != null && String(sample[k]).trim() !== "");
  const looksMultiplex = hasField(
    "cinema",
    "Cinema",
    "cinemaCategory",
    "CinemaCategory",
    "screenCode",
    "ScreenCode",
    "audiNum",
    "AudiNum",
    "seatingCapacity",
    "SeatingCapacity",
    "casCodes",
    "CASCodes",
  );
  const looksDigital = hasField(
    "mediaSiteCode",
    "MediaSiteCode",
    "propertyName",
    "PropertyName",
    "numberOfScreens",
    "NumberOfScreens",
  );
  if (
    productLine?.isMultiplexMedia ||
    (!productLine?.isHoardingMedia &&
      !productLine?.isDigitalAdsMedia &&
      looksMultiplex)
  ) {
    return { cols: MULTIPLEX_PI_COLS, title: "Selected multiplex screens" };
  }
  if (
    productLine?.isDigitalAdsMedia ||
    (!productLine?.isHoardingMedia && looksDigital)
  ) {
    return {
      cols: DIGITAL_ADS_PI_COLS,
      title: "Selected DOOH / digital-ad screens",
    };
  }
  return { cols: HOARDING_PI_COLS, title: "Selected hoarding sites" };
}
